import { Injectable, inject } from '@angular/core';
import {
    Statistic2DataRepresentationType,
    Statistic2DisplayType,
    Statistic2ObjectSelectionType,
    Statistic2ReportType,
    Statistic2ResultsPerType,
    Statistic2TimePeriod,
    Statistic2UserSelectionType,
} from '@fi-app/statistic2/statistic2-types';
import { IStatistic2ReportItem, IStatistic2ReportParameter } from '@fi-app/statistic2/statistic2';
import { AppTextService } from '@fi-shared/app-text';
import { ObjectTypes } from '@fi-shared/types';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    IStatistic2ReportSchemaItem,
    IStatistic2ReportViewParams,
    IStatisticColumnDef,
    IStatisticReportTableSettings,
} from '@fi-app/statistic2/statistic2-report-view/statistic2-report-view';
import {
    Statistic2ReportColumn,
    Statistic2ReportColumnsDef,
} from '@fi-app/statistic2/statistic2-report-view/statistic2-report-column';
import { ILightWeightObject } from '@fi-shared/models';
import { FiDeviceType } from '@fi-app/core/device-type';
import { getObjectViewStateData } from '@fi-shared/utils/get-object-view-state-data';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiDateFormatOptions } from '@fi-shared/utils/format-date';

@Injectable()
export class Statistic2HelperService {
    selectedReportItemIndex: number;

    private readonly reportTypeTextIdMap: Readonly<Record<Statistic2ReportType, string>> = {
        [Statistic2ReportType.UserLogins]: 'statistic.user_logins',
        [Statistic2ReportType.ReadRating]: 'statistic.read_rating',
        [Statistic2ReportType.FileViews]: 'statistic.file_views',
    };
    private readonly readRatingForTextIdMap: Readonly<Record<Statistic2ObjectSelectionType, string>> = {
        [Statistic2ObjectSelectionType.SelectedItems]: 'statistic.selected_pages',
        [Statistic2ObjectSelectionType.SelectedTags]: 'statistic.selected_tag',
        [Statistic2ObjectSelectionType.AllItems]: 'statistic.all_pages',
    };
    private readonly fileViewsForTextIdMap: Readonly<Record<Statistic2ObjectSelectionType, string>> = {
        [Statistic2ObjectSelectionType.SelectedItems]: 'statistic.selected_files',
        [Statistic2ObjectSelectionType.SelectedTags]: 'statistic.selected_tag',
        [Statistic2ObjectSelectionType.AllItems]: 'statistic.all_files',
    };
    private readonly resultsPerTextIdMap: Readonly<
        Record<
            Exclude<Statistic2ResultsPerType, Statistic2ResultsPerType.NoGroping | Statistic2ResultsPerType.Detailed>,
            string
        >
    > = {
        [Statistic2ResultsPerType.Area]: 'common.area',
        [Statistic2ResultsPerType.Unit]: 'common.unit',
        [Statistic2ResultsPerType.User]: 'user.user',
        [Statistic2ResultsPerType.Object]: 'common.page',
        [Statistic2ResultsPerType.File]: 'common.file',
    };
    private readonly timePeriodTextIdMap: Readonly<Record<Statistic2TimePeriod, string>> = {
        [Statistic2TimePeriod.LastWeek]: 'common.last_7_days',
        [Statistic2TimePeriod.LastMonth]: 'common.last_month',
        [Statistic2TimePeriod.LastYear]: 'common.last_year',
        [Statistic2TimePeriod.Custom]: 'common.custom',
    };
    private readonly userSelectionTextIdMap: Readonly<Record<Statistic2UserSelectionType, string>> = {
        [Statistic2UserSelectionType.EveryOne]: 'statistic.everyone',
        [Statistic2UserSelectionType.Areas]: 'common.areas',
        [Statistic2UserSelectionType.BusinessUnits]: 'common.units',
        [Statistic2UserSelectionType.Users]: 'common.colleagues',
        [Statistic2UserSelectionType.Roles]: 'common.roles',
    };

    private appTextService = inject(AppTextService);
    private fiDialogsService = inject(FiDialogsService);
    private fiStateService = inject(FiStateService);
    private fiFormatDateService = inject(FiFormatDateService);

    getParameterForNewReport(): IStatistic2ReportParameter {
        return {
            ReportType: Statistic2ReportType.UserLogins,
            UserSelectionType: Statistic2UserSelectionType.EveryOne,
            ObjectSelectionType: Statistic2ObjectSelectionType.SelectedItems,
            TimePeriod: Statistic2TimePeriod.LastWeek,
            AreaIds: [],
            BusinessUnitIds: [],
            UserIds: [],
            RoleIds: [],
            TagIds: [],
            ObjectIds: [],
            ShowResultsPerType: Statistic2ResultsPerType.Area,
            DataRepresentationType: Statistic2DataRepresentationType.Number,
            DisplayType: Statistic2DisplayType.BarChart,
            Name: '',
            ShowOnDashboard: true,
        };
    }

    getReportTypeText(reportType: Statistic2ReportType) {
        return this.appTextService.get(this.reportTypeTextIdMap[reportType]);
    }

    getObjectsSelectionText(objectSelectionType: Statistic2ObjectSelectionType, reportType: Statistic2ReportType) {
        if (reportType === Statistic2ReportType.ReadRating) {
            return this.appTextService.get(this.readRatingForTextIdMap[objectSelectionType]);
        }
        if (reportType === Statistic2ReportType.FileViews) {
            return this.appTextService.get(this.fileViewsForTextIdMap[objectSelectionType]);
        }
        return '';
    }

    getUserSelectionText(userSelectionType: Statistic2UserSelectionType) {
        return this.appTextService.get(this.userSelectionTextIdMap[userSelectionType]);
    }

    getShowResultsPerText(resultsPerType: Statistic2ResultsPerType): string {
        return this.appTextService.get(this.resultsPerTextIdMap[resultsPerType]);
    }

    getReportPeriodText(reportParameter: IStatistic2ReportParameter): string {
        if (reportParameter.ReportType !== Statistic2ReportType.UserLogins) {
            return '-';
        }
        if (reportParameter.TimePeriod === Statistic2TimePeriod.Custom) {
            return (
                this.fiFormatDateService.formatDate(reportParameter.TimePeriodStartDate, FiDateFormatOptions) +
                ' - ' +
                this.fiFormatDateService.formatDate(reportParameter.TimePeriodEndDate, FiDateFormatOptions)
            );
        }
        return this.getTimePeriodText(reportParameter.TimePeriod);
    }

    getTimePeriodText(timePeriod: Statistic2TimePeriod) {
        return this.appTextService.get(this.timePeriodTextIdMap[timePeriod]);
    }

    /*
    method used in reports list ('for' column) and on dashboard for report's description
     */
    getReportForText(reportParameter: IStatistic2ReportParameter) {
        if (reportParameter.ReportType === Statistic2ReportType.UserLogins) {
            return this.getUserSelectionText(reportParameter.UserSelectionType);
        } else {
            return this.getObjectsSelectionText(reportParameter.ObjectSelectionType, reportParameter.ReportType);
        }
        return '-';
    }

    showPieChartDisplayType(reportParameter: IStatistic2ReportParameter) {
        return reportParameter.ReportType === Statistic2ReportType.UserLogins;
    }

    showEditReportDialog(
        headerTextId: string,
        componentRef: {
            new (...args: any[]): FiDialogsContentBase<IStatistic2ReportParameter, IStatistic2ReportParameter>;
        },
        reportParameter: IStatistic2ReportParameter,
    ) {
        return this.fiDialogsService.custom({
            headerText: this.appTextService.get(headerTextId),
            contentComponentRef: componentRef,
            contentComponentData: reportParameter,
            config: {
                minWidth: '640px',
                panelClass: 'statistic2-report-parameter-edit-dialog',
                disableClose: true,
            },
        });
    }

    showRemoveWidgetFromDashboardDialog(): IFiDialogResult<any> {
        return this.fiDialogsService.confirm({
            headerText: this.appTextService.get('common.warning'),
            messageText: this.appTextService.get('statistic.remove_widget_from_dashboard_confirm'),
            yesButtonText: this.appTextService.get('common.remove'),
        });
    }

    showDeleteReportDialog(): IFiDialogResult<any> {
        return this.fiDialogsService.confirm({
            headerText: this.appTextService.get('common.warning'),
            messageText: this.appTextService.get('statistic.delete_report_confirm'),
            yesButtonText: this.appTextService.get('common.delete'),
        });
    }

    openExtendedReportView(reportParameter: IStatistic2ReportParameter) {
        const viewStateData = getObjectViewStateData(ObjectTypes.Statistic2, reportParameter.Id);
        this.fiStateService.go(viewStateData.sref, viewStateData.srefParams);
    }

    isUserLogins(reportParameter: IStatistic2ReportParameter) {
        return reportParameter.ReportType === Statistic2ReportType.UserLogins;
    }
    isUserLoginsPerArea(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isUserLogins(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Area
        );
    }
    isUserLoginsPerUnit(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isUserLogins(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Unit
        );
    }
    isUserLoginsPerUser(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isUserLogins(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.User
        );
    }
    isUserLoginsDetailed(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isUserLogins(reportParameter) &&
            reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Detailed
        );
    }
    isUserLoginsNoGrouping(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isUserLogins(reportParameter) &&
            reportParameter.ShowResultsPerType === Statistic2ResultsPerType.NoGroping
        );
    }

    isReadRating(reportParameter: IStatistic2ReportParameter) {
        return reportParameter.ReportType === Statistic2ReportType.ReadRating;
    }
    isReadRatingPerArea(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isReadRating(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Area
        );
    }
    isReadRatingPerUnit(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isReadRating(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Unit
        );
    }
    isReadRatingPerDocument(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isReadRating(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Object
        );
    }
    isReadRatingNoGrouping(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isReadRating(reportParameter) &&
            reportParameter.ShowResultsPerType === Statistic2ResultsPerType.NoGroping
        );
    }
    isReadRatingPerUser(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isReadRating(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.User
        );
    }

    isFileViews(reportParameter: IStatistic2ReportParameter) {
        return reportParameter.ReportType === Statistic2ReportType.FileViews;
    }
    isFileViewsPerArea(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isFileViews(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Area
        );
    }
    isFileViewsPerUnit(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isFileViews(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Unit
        );
    }
    isFileViewsPerMediaFile(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isFileViews(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Object
        );
    }
    isFileViewsNoGrouping(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isFileViews(reportParameter) &&
            reportParameter.ShowResultsPerType === Statistic2ResultsPerType.NoGroping
        );
    }
    isFileViewsPerUser(reportParameter: IStatistic2ReportParameter) {
        return (
            this.isFileViews(reportParameter) && reportParameter.ShowResultsPerType === Statistic2ResultsPerType.User
        );
    }

    isUserDetailedReport(reportParameter: IStatistic2ReportParameter) {
        return reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Detailed;
    }
    isUserNoGroupingReport(reportParameter: IStatistic2ReportParameter) {
        return reportParameter.ShowResultsPerType === Statistic2ResultsPerType.NoGroping;
    }

    isShowDataPresentationType(reportParameter: IStatistic2ReportParameter) {
        return (
            (reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Area ||
                reportParameter.ShowResultsPerType === Statistic2ResultsPerType.Unit) &&
            this.isUserLogins(reportParameter)
        );
    }

    setSelectedReportItemIndex(selectedReportItemIndex: number) {
        this.selectedReportItemIndex = selectedReportItemIndex;
    }

    getSelectedReportItemIndex() {
        return this.selectedReportItemIndex;
    }

    generateTableData(reportViewParams: IStatistic2ReportViewParams): IStatisticReportTableSettings[] {
        return reportViewParams.schema.map((item: IStatistic2ReportSchemaItem) =>
            this.getStatisticReportTableItem(item, reportViewParams),
        );
    }

    private getStatisticReportTableItem(
        item: IStatistic2ReportSchemaItem,
        reportViewParams: IStatistic2ReportViewParams,
    ): IStatisticReportTableSettings {
        const matColumnDef: string = this.getMatColumnDef(item);

        return {
            matColumnDef: matColumnDef,
            headerText: item.columnHeader,
            headerTooltipText: item.columnTooltip,
            columnType: item.columnType,
            cellClass: this.getTableCellClass(item, reportViewParams) + ' ' + 'column-show-mobile',
            isSortDisabled: this.isColumnType(item.columnType, [
                Statistic2ReportColumn.Roles,
                Statistic2ReportColumn.Icon,
            ]),
            isProfileIconShown: this.isColumnType(item.columnType, [Statistic2ReportColumn.Icon]),
            isDateValue: this.isColumnType(item.columnType, [
                Statistic2ReportColumn.StartDate,
                Statistic2ReportColumn.EndDate,
            ]),
            getItemValue: (cellItemData: IStatistic2ReportItem) => {
                if (this.isColumnType(item.columnType, [Statistic2ReportColumn.Roles])) {
                    return cellItemData.Roles.map((role: ILightWeightObject) => role.Name).join(', ');
                }
                if (
                    this.isColumnType(item.columnType, [
                        Statistic2ReportColumn.PercentageValue,
                        Statistic2ReportColumn.Value,
                        Statistic2ReportColumn.NewItemsValue,
                    ])
                ) {
                    return cellItemData[matColumnDef] + (item.isPercentage ? ' %' : '');
                }
                if (this.isColumnType(item.columnType, [Statistic2ReportColumn.TimeOnPage])) {
                    return this.getTimeOnPageValue(cellItemData);
                }
                if (this.isColumnType(item.columnType, [Statistic2ReportColumn.DeviceType])) {
                    return this.getDeviceTypeLabel(cellItemData);
                }

                return cellItemData[matColumnDef];
            },
            getUserProfile: (item: IStatistic2ReportItem) => item.UserProfile,
        };
    }

    private getTableCellClass(
        item: IStatistic2ReportSchemaItem,
        reportViewParams: IStatistic2ReportViewParams,
    ): string {
        switch (item.columnType) {
            case Statistic2ReportColumn.Icon:
                return 'CcmListItem-Icon';
            case Statistic2ReportColumn.ObjectName:
                return this.getObjectNameColumnClass(item.columnHeader);
            case Statistic2ReportColumn.Roles:
                return this.getRolesClass(reportViewParams);
            case Statistic2ReportColumn.MobileAppLoginsCount:
                return 'CcmList-Column-Narrow fi-cell-right-aligned';
            case Statistic2ReportColumn.WebLoginsCount:
                return 'CcmList-Column-Narrow fi-cell-right-aligned';
            case Statistic2ReportColumn.Value:
                return 'CcmList-Column-Narrow fi-cell-right-aligned';
            case Statistic2ReportColumn.NewItemsValue:
                return this.isUserLogins(reportViewParams.reportParameter)
                    ? 'column-show-mobile fi-cell-right-aligned'
                    : 'fi-cell-right-aligned';
            case Statistic2ReportColumn.EndDate:
                return 'CcmList-Column-small-padding';
            case Statistic2ReportColumn.LatestActivity:
                return 'CcmList-Column-Narrow';
            case Statistic2ReportColumn.CurrentUnitVisitsColumn:
                return 'CcmList-Column-Narrow fi-cell-right-aligned';
            case Statistic2ReportColumn.VisitsColumn:
                return 'fi-cell-right-aligned';
            case Statistic2ReportColumn.FileViewsCount:
            case Statistic2ReportColumn.FileDownloadsCount:
                return this.getFileColumnClass(reportViewParams);
        }
        return '';
    }

    private getObjectNameColumnClass(columnHeader: string): string {
        return columnHeader === this.getShowResultsPerText(Statistic2ResultsPerType.User)
            ? 'CcmList-Column-Narrow'
            : 'CcmListItem-Name';
    }

    private getFileColumnClass(reportViewParams: IStatistic2ReportViewParams): string {
        if (reportViewParams.schema.find((schemaItem) => schemaItem.columnType === Statistic2ReportColumn.StartDate))
            return 'CcmList-Column-Narrow';

        return 'CcmList-Column-Narrow fi-cell-right-aligned';
    }

    private getRolesClass(reportViewParams: IStatistic2ReportViewParams) {
        if (this.isUserLogins(reportViewParams.reportParameter) && !reportViewParams.showLatestActivityColumn)
            return 'CcmList-Column-ExtraWide';
        if (reportViewParams.showLatestActivityColumn) return '';
        return 'CcmList-Column-Wide';
    }

    private getTimeOnPageValue(cellItemData: IStatistic2ReportItem): string {
        let diff = <any>new Date(cellItemData.EndDate) - <any>new Date(cellItemData.StartDate);
        const ms = diff % 1000;
        diff = (diff - ms) / 1000;
        const ss = diff % 60;
        diff = (diff - ss) / 60;
        const mm = diff % 60;
        diff = (diff - mm) / 60;
        const hh = diff;
        return ('0' + hh).slice(-2) + ':' + ('0' + mm).slice(-2) + ':' + ('0' + ss).slice(-2);
    }

    private getDeviceTypeLabel(cellItemData: IStatistic2ReportItem): string {
        return this.appTextService.get(
            cellItemData.DeviceType === FiDeviceType.MobileApp ? 'statistic.mobile_app' : 'statistic.browser',
        );
    }

    private getMatColumnDef(item: IStatistic2ReportSchemaItem): string {
        return Statistic2ReportColumnsDef.find((statisticColumnDef: IStatisticColumnDef) => {
            return statisticColumnDef.id === item.columnType;
        })?.propertyName;
    }

    isColumnType(itemColumnType: Statistic2ReportColumn, columnTypes: Statistic2ReportColumn[]): boolean {
        return columnTypes.some((type: Statistic2ReportColumn) => type === itemColumnType);
    }
}
